import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { getSyllabus } from "@/lib/api";
import { fadeUp, viewportOnce } from "@/lib/motion";
import { Check, BookOpen } from "lucide-react";

export const Syllabus = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    getSyllabus().then(setData).catch(() => {});
  }, []);

  const chapters = data?.chapters || [];

  return (
    <section
      id="syllabus"
      data-testid="syllabus-section"
      className="relative border-y border-cream/10 bg-ink-elevated py-20 md:py-32"
    >
      <div className="container-tasha">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end"
        >
          <div className="max-w-2xl">
            <span className="overline flex items-center gap-2">
              <BookOpen size={15} /> {data?.week_label || "This Week's Class"}
            </span>
            <h2 className="mt-5 font-serif text-4xl font-semibold leading-tight tracking-tight text-cream md:text-6xl">
              This week's <span className="font-script text-gold">syllabus</span>
            </h2>
            <p className="mt-6 font-sans text-lg font-light text-cream/70">
              A living curriculum — refreshed every week. Here's exactly what
              we'll cover in the next live session.
            </p>
          </div>
          <div className="rounded-full border border-gold/30 px-5 py-2 font-sans text-sm uppercase tracking-widest text-gold">
            {data?.session_date || "Thursday · 7PM EST"}
          </div>
        </motion.div>

        {chapters.length > 0 && (
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            className="mt-14"
          >
            <Tabs defaultValue={chapters[0].number} className="w-full" data-testid="syllabus-tabs">
              <TabsList className="flex h-auto w-full flex-wrap justify-start gap-2 bg-transparent p-0">
                {chapters.map((c) => (
                  <TabsTrigger
                    key={c.number}
                    value={c.number}
                    data-testid={`syllabus-tab-${c.number}`}
                    className="rounded-none border-b-2 border-transparent bg-transparent px-5 py-3 font-sans text-sm uppercase tracking-widest text-cream/50 transition-all data-[state=active]:border-gold data-[state=active]:bg-transparent data-[state=active]:text-cream data-[state=active]:shadow-none"
                  >
                    <span className="mr-2 font-serif text-lg text-gold">{c.number}</span>
                    <span className="hidden sm:inline">{c.title}</span>
                  </TabsTrigger>
                ))}
              </TabsList>

              {chapters.map((c) => (
                <TabsContent key={c.number} value={c.number} className="mt-10">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={c.number}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                      className="grid gap-10 md:grid-cols-[0.9fr_1.1fr]"
                    >
                      <div>
                        <span className="gold-text-gradient font-serif text-8xl font-semibold leading-none">
                          {c.number}
                        </span>
                        <h3 className="mt-4 font-serif text-3xl font-semibold text-cream md:text-4xl">
                          {c.title}
                        </h3>
                        <p className="mt-5 font-sans text-lg font-light leading-relaxed text-cream/70">
                          {c.summary}
                        </p>
                      </div>
                      <ul className="flex flex-col gap-4">
                        {c.points.map((p, i) => (
                          <motion.li
                            key={i}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.1 + i * 0.08, duration: 0.5 }}
                            className="flex items-start gap-4 rounded-xl border border-cream/10 bg-ink/60 p-5"
                          >
                            <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-gold/15 text-gold">
                              <Check size={14} />
                            </span>
                            <span className="font-sans text-base font-light leading-relaxed text-cream/85">
                              {p}
                            </span>
                          </motion.li>
                        ))}
                      </ul>
                    </motion.div>
                  </AnimatePresence>
                </TabsContent>
              ))}
            </Tabs>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default Syllabus;
